import React, { useCallback } from 'react';
import type { OptimizedResume, WorkExperience } from '../types';
import { MailIcon } from './icons/MailIcon';
import { PhoneIcon } from './icons/PhoneIcon';
import { LinkedinIcon } from './icons/LinkedinIcon';
import { MapPinIcon } from './icons/MapPinIcon';

interface ResumePreviewProps {
    // 修改：允许 resume 为可选，以处理初始加载状态
    resume?: OptimizedResume;
    onResumeChange: (updatedResume: OptimizedResume) => void;
}

const EditableField: React.FC<{ value: string; onUpdate: (newValue: string) => void; tag?: React.ElementType; className?: string; placeholder?: string; }> = ({ value, onUpdate, tag: Tag = 'div', className, placeholder }) => {
    return (
        <Tag
            contentEditable
            suppressContentEditableWarning
            onBlur={(e) => onUpdate(e.currentTarget.textContent || '')}
            className={`focus:outline-indigo-300 focus:bg-indigo-50/50 rounded-sm px-1 -mx-1 empty:before:content-[attr(data-placeholder)] empty:before:text-slate-400 empty:before:italic ${className}`}
            dangerouslySetInnerHTML={{ __html: value }}
            data-placeholder={placeholder}
        />
    );
};

const ResumePreview: React.FC<ResumePreviewProps> = ({ resume, onResumeChange }) => {

    // 1. 核心修改：增加加载状态判断。如果 resume 还没从 AI 接口返回，显示英文提示
    if (!resume || !resume.personalDetails) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[600px] bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center max-w-4xl mx-auto">
                <div className="w-12 h-12 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Optimizing Your Resume...</h3>
                <p className="text-slate-500 max-w-sm">
                    Our AI is refining your content for maximum professional impact. This usually takes a few seconds.
                </p>
            </div>
        );
    }

    // --- 以下是你原有的逻辑，无需改动，只需确保在判断语句之后执行 ---

    const handleFieldChange = useCallback(<T extends keyof OptimizedResume>(field: T, value: OptimizedResume[T]) => {
        onResumeChange({ ...resume, [field]: value });
    }, [resume, onResumeChange]);
    
    const handleNestedFieldChange = useCallback(<
        T extends 'workExperience' | 'education',
        K extends keyof OptimizedResume[T][number]
    >(
        field: T,
        index: number,
        subField: K,
        value: OptimizedResume[T][number][K]
    ) => {
        const newArray = [...resume[field]];
        newArray[index] = { ...newArray[index], [subField]: value };
        handleFieldChange(field, newArray);
    }, [resume, handleFieldChange]);
    
    const handleResponsibilitiesChange = (expIndex: number, respIndex: number, value: string) => {
        const newExperience = [...resume.workExperience];
        newExperience[expIndex].responsibilities[respIndex] = value;
        handleFieldChange('workExperience', newExperience);
    };

    const handlePersonalDetailChange = <K extends keyof OptimizedResume['personalDetails']>(field: K, value: OptimizedResume['personalDetails'][K]) => {
        handleFieldChange('personalDetails', { ...resume.personalDetails, [field]: value });
    };

    return (
        <div id="resume-preview" className="p-4 sm:p-8 bg-white text-sm text-slate-800 font-normal leading-relaxed aspect-[8.5/11] max-w-4xl mx-auto transform scale-90 md:scale-100 origin-top shadow-xl border border-slate-100">
            <header className="text-center mb-8">
                <EditableField value={resume.personalDetails.name} onUpdate={val => handlePersonalDetailChange('name', val)} tag="h1" className="text-4xl font-bold text-slate-900 tracking-tight" placeholder="Your Name" />
                <div className="mt-3 flex justify-center items-center gap-x-4 gap-y-1 text-xs text-slate-600 flex-wrap">
                    <div className="flex items-center gap-1.5">
                        <MailIcon className="w-3 h-3" />
                        <EditableField value={resume.personalDetails.email} onUpdate={val => handlePersonalDetailChange('email', val)} placeholder="your.email@example.com" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <PhoneIcon className="w-3 h-3" />
                        <EditableField value={resume.personalDetails.phone} onUpdate={val => handlePersonalDetailChange('phone', val)} placeholder="(123) 456-7890" />
                    </div>
                    <div className="flex items-center gap-1.5">
                        <MapPinIcon className="w-3 h-3" />
                        <EditableField value={resume.personalDetails.location} onUpdate={val => handlePersonalDetailChange('location', val)} placeholder="City, Country" />
                    </div>
                    {resume.personalDetails.linkedin &&
                        <div className="flex items-center gap-1.5">
                            <LinkedinIcon className="w-3 h-3" />
                            <EditableField value={resume.personalDetails.linkedin} onUpdate={val => handlePersonalDetailChange('linkedin', val)} placeholder="linkedin.com/in/yourprofile" />
                        </div>
                    }
                </div>
            </header>

            <section>
                <h2 className="text-sm font-bold tracking-widest uppercase text-indigo-700 pb-1 border-b-2 border-slate-200 mb-3">Summary</h2>
                <EditableField value={resume.professionalSummary} onUpdate={val => handleFieldChange('professionalSummary', val)} placeholder="A brief professional summary..." />
            </section>    

            <section className="mt-6">
                <h2 className="text-sm font-bold tracking-widest uppercase text-indigo-700 pb-1 border-b-2 border-slate-200 mb-3">Experience</h2>
                <div className="space-y-4">
                    {resume.workExperience.map((exp, expIndex) => (
                        <div key={expIndex}>
                            <div className="flex justify-between items-baseline">
                                <h3 className="font-bold text-slate-900">
                                    <EditableField value={exp.jobTitle} onUpdate={val => handleNestedFieldChange('workExperience', expIndex, 'jobTitle', val)} placeholder="Job Title" />
                                </h3>
                                <div className="text-xs text-slate-500 font-medium">
                                    <EditableField value={exp.startDate} onUpdate={val => handleNestedFieldChange('workExperience', expIndex, 'startDate', val)} placeholder="Mon YYYY" /> - <EditableField value={exp.endDate} onUpdate={val => handleNestedFieldChange('workExperience', expIndex, 'endDate', val)} placeholder="Mon YYYY" />
                                </div>
                            </div>
                            <div className="flex justify-between items-baseline text-sm">
                                <p className="text-indigo-800 font-semibold">
                                    <EditableField value={exp.company} onUpdate={val => handleNestedFieldChange('workExperience', expIndex, 'company', val)} placeholder="Company Name" />
                                </p>
                                <p className="text-xs text-slate-500 font-medium">
                                    <EditableField value={exp.location} onUpdate={val => handleNestedFieldChange('workExperience', expIndex, 'location', val)} placeholder="City, Country" />
                                </p>
                            </div>
                            <ul className="mt-1 list-disc list-outside pl-5 space-y-1">
                                {exp.responsibilities.map((resp, respIndex) => (
                                    <li key={respIndex}>
                                        <EditableField value={resp} onUpdate={val => handleResponsibilitiesChange(expIndex, respIndex, val)} placeholder="Responsibility or achievement..." />
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>

            <section className="mt-6">
                <h2 className="text-sm font-bold tracking-widest uppercase text-indigo-700 pb-1 border-b-2 border-slate-200 mb-3">Education</h2>
                {resume.education.map((edu, index) => (
                    <div key={index} className="flex justify-between items-baseline">
                        <div>
                            <h3 className="font-bold text-slate-900">
                                <EditableField value={edu.institution} onUpdate={val => handleNestedFieldChange('education', index, 'institution', val)} placeholder="University Name" />
                            </h3>
                            <p className="text-sm">
                                <EditableField value={edu.degree} onUpdate={val => handleNestedFieldChange('education', index, 'degree', val)} placeholder="Degree and Major" />
                            </p>
                        </div>
                        <p className="text-xs text-slate-500 font-medium">
                            <EditableField value={edu.graduationDate} onUpdate={val => handleNestedFieldChange('education', index, 'graduationDate', val)} placeholder="Month YYYY" />
                        </p>
                    </div>
                ))}
            </section>
            
            <section className="mt-6">
                <h2 className="text-sm font-bold tracking-widest uppercase text-indigo-700 pb-1 border-b-2 border-slate-200 mb-3">Skills</h2>
                <EditableField 
                    value={resume.skills.join(', ')} 
                    onUpdate={val => handleFieldChange('skills', val.split(',').map(s => s.trim()))} 
                    placeholder="List your skills, separated by commas"
                />
            </section>
        </div>
    );
};

export default ResumePreview;
